import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toPaise } from '@vns/core';
import { supabase } from './supabase';
import { useSession } from './session';

/**
 * Suppliers, sub-contractors and materials.
 *
 * Suppliers and sub-contractors both carry a multi-select — material types and
 * trades respectively — stored in junction tables. Creating one is therefore two
 * writes: the record, then its links.
 */

function unwrap<T>({ data, error }: { data: T; error: { message: string } | null }): NonNullable<T> {
  if (error) throw new Error(error.message);
  if (data === null || data === undefined) throw new Error('No data returned');
  return data as NonNullable<T>;
}

/** PostgREST reads a comma as an OR separator, so it must not reach the pattern. */
const safeTerm = (s: string) => s.trim().replace(/[%,()]/g, ' ');

export interface BankFields {
  bank_name?: string;
  bank_holder?: string;
  bank_account_no?: string;
  bank_ifsc?: string;
  bank_branch?: string;
}

function bankPayload(v: BankFields) {
  return {
    bank_name: v.bank_name?.trim() || null,
    bank_holder: v.bank_holder?.trim() || null,
    bank_account_no: v.bank_account_no?.trim() || null,
    bank_ifsc: v.bank_ifsc?.trim().toUpperCase() || null,
    bank_branch: v.bank_branch?.trim() || null,
  };
}

/* ------------------------------- suppliers ------------------------------- */

export interface SupplierRow {
  id: string;
  name: string;
  contact_person: string | null;
  phone: string;
  city: string | null;
  gst_number: string | null;
  materialTypes: string[];
}

export function useSuppliers(search: string, materialType?: string) {
  return useQuery({
    queryKey: ['suppliers', search, materialType ?? ''],
    queryFn: async (): Promise<SupplierRow[]> => {
      let q = supabase
        .from('suppliers')
        .select('id, name, contact_person, phone, city, gst_number, supplier_material_types(material_categories(name))')
        .is('deleted_at', null)
        .order('name');

      const term = safeTerm(search);
      if (term) q = q.or(`name.ilike.%${term}%,phone.ilike.%${term}%,gst_number.ilike.%${term}%,city.ilike.%${term}%`);

      const rows = unwrap(await q);
      let suppliers: SupplierRow[] = rows.map((r) => ({
        id: r.id,
        name: r.name,
        contact_person: r.contact_person,
        phone: r.phone,
        city: r.city,
        gst_number: r.gst_number,
        materialTypes: (r.supplier_material_types as unknown as Array<{ material_categories: { name: string } | null }>)
          .map((x) => x.material_categories?.name)
          .filter((n): n is string => !!n),
      }));

      if (materialType) {
        suppliers = suppliers.filter((s) => s.materialTypes.includes(materialType));
      }

      return suppliers;
    },
  });
}

export interface NewSupplier extends BankFields {
  name: string;
  contact_person?: string;
  phone: string;
  email?: string;
  address?: string;
  city?: string;
  gst_number?: string;
  remarks?: string;
  materialCategoryIds?: string[];
  materialIds?: string[];
}

export function useCreateSupplier() {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);

  return useMutation({
    mutationFn: async (v: NewSupplier) => {
      if (!actor) throw new Error('Not signed in');
      const supplier = unwrap(
        await supabase.from('suppliers').insert({
          company_id: actor.companyId,
          name: v.name.trim(),
          contact_person: v.contact_person?.trim() || null,
          phone: v.phone.trim(),
          email: v.email?.trim() || null,
          address: v.address?.trim() || null,
          city: v.city?.trim() || null,
          gst_number: v.gst_number?.trim().toUpperCase() || null,
          remarks: v.remarks?.trim() || null,
          ...bankPayload(v),
          created_by: actor.id,
        }).select('id').single(),
      );

      const catIds = v.materialCategoryIds ?? [];
      if (catIds.length) {
        const { error } = await supabase.from('supplier_material_types').insert(
          catIds.map((id) => ({
            supplier_id: supplier.id,
            material_category_id: id,
            company_id: actor.companyId,
          })),
        );
        if (error) throw new Error(error.message);
      }
      return supplier;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['suppliers'] }),
        qc.invalidateQueries({ queryKey: ['lookups'] }),
      ]);
    },
  });
}

/* ---------------------------- sub-contractors ---------------------------- */

export interface SubcontractorRow {
  id: string;
  name: string;
  phone: string;
  city: string | null;
  gst_number: string | null;
  workTypes: string[];
}

export function useSubcontractors(search: string) {
  return useQuery({
    queryKey: ['subcontractors', search],
    queryFn: async (): Promise<SubcontractorRow[]> => {
      let q = supabase
        .from('subcontractors')
        .select('id, name, phone, city, gst_number, subcontractor_work_types(work_types(name))')
        .is('deleted_at', null)
        .order('name');

      const term = safeTerm(search);
      if (term) q = q.or(`name.ilike.%${term}%,phone.ilike.%${term}%,city.ilike.%${term}%`);

      const rows = unwrap(await q);
      return rows.map((r) => ({
        id: r.id,
        name: r.name,
        phone: r.phone,
        city: r.city,
        gst_number: r.gst_number,
        workTypes: (r.subcontractor_work_types as unknown as Array<{ work_types: { name: string } | null }>)
          .map((x) => x.work_types?.name)
          .filter((n): n is string => !!n),
      }));
    },
  });
}

export interface NewSubcontractor extends BankFields {
  name: string;
  phone: string;
  email?: string;
  address?: string;
  city?: string;
  gst_number?: string;
  remarks?: string;
  workTypeIds: string[];
}

export function useCreateSubcontractor() {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);

  return useMutation({
    mutationFn: async (v: NewSubcontractor) => {
      if (!actor) throw new Error('Not signed in');
      const sub = unwrap(
        await supabase.from('subcontractors').insert({
          company_id: actor.companyId,
          name: v.name.trim(),
          phone: v.phone.trim(),
          email: v.email?.trim() || null,
          address: v.address?.trim() || null,
          city: v.city?.trim() || null,
          gst_number: v.gst_number?.trim().toUpperCase() || null,
          remarks: v.remarks?.trim() || null,
          ...bankPayload(v),
          created_by: actor.id,
        }).select('id').single(),
      );

      if (v.workTypeIds.length) {
        const { error } = await supabase.from('subcontractor_work_types').insert(
          v.workTypeIds.map((id) => ({
            subcontractor_id: sub.id,
            work_type_id: id,
            company_id: actor.companyId,
          })),
        );
        if (error) throw new Error(error.message);
      }
      return sub;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['subcontractors'] }),
        qc.invalidateQueries({ queryKey: ['lookups'] }),
      ]);
    },
  });
}

/* ------------------------------- materials ------------------------------- */

export interface MaterialRow {
  id: string;
  name: string;
  brand: string | null;
  unitName: string | null;
  ratePaise: number;
  supplierName: string | null;
  /** Purchased minus used, across every project. */
  stock: number;
  openingQty: number;
  // Ids as well as names: the table shows names, the edit form needs ids.
  unitId: string | null;
  supplierId: string | null;
  categoryId?: string | null;
  categoryName?: string | null;
}

export function useMaterials(search: string) {
  return useQuery({
    queryKey: ['materials', search],
    queryFn: async (): Promise<MaterialRow[]> => {
      let q = supabase
        .from('materials')
        .select('id, name, brand, rate_paise, opening_qty, unit_id, supplier_id, category_id, units(short_name), suppliers(name), material_categories(name)')
        .is('deleted_at', null)
        .order('name');

      const term = safeTerm(search);
      if (term) q = q.or(`name.ilike.%${term}%,brand.ilike.%${term}%`);

      const rows = unwrap(await q);

      // Stock on hand = opening + approved purchases - approved usage, netted
      // here in one pass rather than a query per material.
      const [purchases, usage] = await Promise.all([
        supabase.from('material_purchases').select('material_id, quantity')
          .eq('approval_status', 'approved').is('deleted_at', null),
        supabase.from('material_usage').select('material_id, quantity')
          .eq('approval_status', 'approved').is('deleted_at', null),
      ]);
      if (purchases.error) throw new Error(purchases.error.message);
      if (usage.error) throw new Error(usage.error.message);

      const bought = new Map<string, number>();
      for (const p of purchases.data ?? []) {
        bought.set(p.material_id, (bought.get(p.material_id) ?? 0) + Number(p.quantity));
      }
      const used = new Map<string, number>();
      for (const u of usage.data ?? []) {
        used.set(u.material_id, (used.get(u.material_id) ?? 0) + Number(u.quantity));
      }

      return rows.map((r) => ({
        id: r.id,
        name: r.name,
        brand: r.brand,
        unitName: (r.units as unknown as { short_name: string } | null)?.short_name ?? null,
        ratePaise: r.rate_paise,
        supplierName: (r.suppliers as unknown as { name: string } | null)?.name ?? null,
        stock: Number(r.opening_qty) + (bought.get(r.id) ?? 0) - (used.get(r.id) ?? 0),
        openingQty: Number(r.opening_qty),
        unitId: r.unit_id,
        supplierId: r.supplier_id,
        categoryId: r.category_id,
        categoryName: (r.material_categories as unknown as { name: string } | null)?.name ?? null,
      }));
    },
  });
}

export interface NewMaterial {
  name: string;
  brand?: string;
  // No category_id: the form no longer offers one. The column and its lookup
  // table remain, so values set earlier are preserved and still listed — they
  // are simply not editable from here.
  unit_id?: string;
  rateRupees: string;
  supplier_id?: string;
  opening_qty?: string;
}

export function useCreateMaterial() {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);

  return useMutation({
    mutationFn: async (v: NewMaterial) => {
      if (!actor) throw new Error('Not signed in');
      return unwrap(
        await supabase.from('materials').insert({
          company_id: actor.companyId,
          name: v.name.trim(),
          brand: v.brand?.trim() || null,
          unit_id: v.unit_id || null,
          rate_paise: v.rateRupees.trim() ? toPaise(v.rateRupees) : 0,
          supplier_id: v.supplier_id || null,
          opening_qty: v.opening_qty?.trim() ? Number(v.opening_qty) : 0,
          created_by: actor.id,
        }).select('id').single(),
      );
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['materials'] }),
        qc.invalidateQueries({ queryKey: ['material'] }),
        qc.invalidateQueries({ queryKey: ['material-full-report'] }),
        qc.invalidateQueries({ queryKey: ['material-report-picker'] }),
        qc.invalidateQueries({ queryKey: ['lookups'] }),
        qc.invalidateQueries({ queryKey: ['project-stock-map'] }),
        qc.invalidateQueries({ queryKey: ['report'] }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    },
  });
}

/* ----------------------------- supplier detail ----------------------------- */

export interface SupplierPurchaseRow {
  id: string;
  date: string;
  projectId: string;
  projectName: string;
  material: string;
  quantity: number;
  unit: string | null;
  ratePaise: number;
  amountPaise: number;
}

export interface SupplierPaymentRow {
  id: string;
  date: string;
  projectName: string | null;
  amountPaise: number;
  mode: string;
  /** What was still owed right after this payment — a running total, not this row's own amount. */
  balancePaise: number;
}

export interface SupplierProjectRow {
  id: string;
  name: string;
  clientName: string;
  status: string;
}

export interface SupplierDetail {
  id: string;
  name: string;
  contactPerson: string | null;
  phone: string;
  city: string | null;
  gstNumber: string | null;
  materialTypes: string[];
  bankName: string | null;
  totalPurchasePaise: number;
  totalPaidPaise: number;
  pendingPaise: number;
  purchases: SupplierPurchaseRow[];
  payments: SupplierPaymentRow[];
  projects: SupplierProjectRow[];
}

/**
 * Everything owed to, and bought from, one supplier — across every project.
 *
 * Only approved, non-deleted rows count, same rule as every ledger in this
 * app: a pending purchase or payment must not move a figure someone will act
 * on.
 */
export function useSupplierDetail(supplierId: string) {
  return useQuery({
    queryKey: ['supplier', supplierId],
    enabled: !!supplierId,
    queryFn: async (): Promise<SupplierDetail> => {
      const supplier = unwrap(
        await supabase
          .from('suppliers')
          .select('id, name, contact_person, phone, city, gst_number, bank_name, supplier_material_types(material_categories(name))')
          .eq('id', supplierId)
          .is('deleted_at', null)
          .single(),
      );

      const purchaseRows = unwrap(
        await supabase
          .from('material_purchases')
          .select('id, purchased_on, quantity, rate_paise, amount_paise, materials(name), units(short_name), projects(id, name)')
          .eq('supplier_id', supplierId)
          .eq('approval_status', 'approved')
          .is('deleted_at', null)
          .order('purchased_on', { ascending: false }),
      );

      const paymentRows = unwrap(
        await supabase
          .from('supplier_payments')
          .select('id, paid_on, amount_paise, mode, projects(name)')
          .eq('supplier_id', supplierId)
          .eq('approval_status', 'approved')
          .is('deleted_at', null)
          .order('paid_on', { ascending: true }),   // ascending: the running balance below needs chronological order
      );

      const purchases: SupplierPurchaseRow[] = purchaseRows.map((r) => ({
        id: r.id,
        date: r.purchased_on,
        projectId: (r.projects as unknown as { id: string; name: string } | null)?.id ?? '',
        projectName: (r.projects as unknown as { id: string; name: string } | null)?.name ?? '—',
        material: (r.materials as unknown as { name: string } | null)?.name ?? '—',
        quantity: Number(r.quantity),
        unit: (r.units as unknown as { short_name: string } | null)?.short_name ?? null,
        ratePaise: r.rate_paise,
        amountPaise: r.amount_paise,
      }));

      const totalPurchasePaise = purchases.reduce((s, p) => s + p.amountPaise, 0);
      const totalPaidPaise = paymentRows.reduce((s, p) => s + p.amount_paise, 0);

      // A running balance — what was still owed right after each payment —
      // built forward through time, then the whole list is shown newest first
      // to match every other ledger in this app.
      let remaining = totalPurchasePaise;
      const payments: SupplierPaymentRow[] = paymentRows
        .map((r) => {
          remaining -= r.amount_paise;
          return {
            id: r.id,
            date: r.paid_on,
            projectName: (r.projects as unknown as { name: string } | null)?.name ?? null,
            amountPaise: r.amount_paise,
            mode: r.mode,
            balancePaise: Math.max(0, remaining),
          };
        })
        .reverse();

      // Every project this supplier has actually delivered to, newest
      // purchase first — matching the prototype this page is built from.
      const seen = new Set<string>();
      const projectIds: string[] = [];
      for (const p of purchases) {
        if (p.projectId && !seen.has(p.projectId)) { seen.add(p.projectId); projectIds.push(p.projectId); }
      }
      let projects: SupplierProjectRow[] = [];
      if (projectIds.length) {
        const projectRows = unwrap(
          await supabase
            .from('projects')
            .select('id, name, status, clients(name)')
            .in('id', projectIds),
        );
        const byId = new Map(projectRows.map((p) => [p.id, p]));
        projects = projectIds
          .map((id) => byId.get(id))
          .filter((p): p is NonNullable<typeof p> => !!p)
          .map((p) => ({
            id: p.id,
            name: p.name,
            clientName: (p.clients as unknown as { name: string } | null)?.name ?? '—',
            status: p.status,
          }));
      }

      return {
        id: supplier.id,
        name: supplier.name,
        contactPerson: supplier.contact_person,
        phone: supplier.phone,
        city: supplier.city,
        gstNumber: supplier.gst_number,
        bankName: supplier.bank_name,
        materialTypes: (supplier.supplier_material_types as unknown as Array<{ material_categories: { name: string } | null }>)
          .map((x) => x.material_categories?.name)
          .filter((n): n is string => !!n),
        totalPurchasePaise,
        totalPaidPaise,
        pendingPaise: Math.max(0, totalPurchasePaise - totalPaidPaise),
        purchases,
        payments,
        projects,
      };
    },
  });
}

/* ----------------------------- material detail ----------------------------- */

export interface MaterialPurchaseRow {
  id: string;
  date: string;
  projectId: string;
  projectName: string;
  supplierName: string;
  invoiceNo: string | null;
  advancePaidPaise: number;
  balancePaise: number;
  quantity: number;
  ratePaise: number;
  amountPaise: number;
}

export interface MaterialUsageRow {
  id: string;
  date: string;
  projectId: string;
  projectName: string;
  quantity: number;
  remarks: string | null;
  /** Stock remaining company-wide right after this entry — a running total. */
  balance: number;
}

export interface MaterialTransferRow {
  id: string;
  date: string;
  fromProjectName: string;
  toProjectName: string;
  quantity: number;
  ratePaise: number | null;
  amountPaise: number | null;
  vehicleNo: string | null;
  driverName: string | null;
  remarks: string | null;
}

export interface MaterialDetail {
  id: string;
  name: string;
  brand: string | null;
  unitName: string | null;
  supplierName: string | null;
  ratePaise: number;
  totalPurchased: number;
  totalUsed: number;
  stockOnHand: number;
  purchases: MaterialPurchaseRow[];
  usage: MaterialUsageRow[];
  transfers: MaterialTransferRow[];
}

/**
 * Everything bought, used and moved for one material, across every project.
 *
 * Stock on hand uses the exact same formula as the Materials list —
 * opening quantity + approved purchases − approved usage — so the number on
 * this page can never disagree with the number that got someone here.
 */
export function useMaterialDetail(materialId: string) {
  return useQuery({
    queryKey: ['material', materialId],
    enabled: !!materialId,
    queryFn: async (): Promise<MaterialDetail> => {
      const material = unwrap(
        await supabase
          .from('materials')
          .select('id, name, brand, rate_paise, opening_qty, units(short_name), suppliers(name)')
          .eq('id', materialId)
          .is('deleted_at', null)
          .single(),
      );

      const [purchaseRows, usageRows, transferRows] = await Promise.all([
        unwrap(
          await supabase
            .from('material_purchases')
            .select('id, purchased_on, quantity, rate_paise, amount_paise, invoice_no, advance_paid_paise, projects(id, name), suppliers(name)')
            .eq('material_id', materialId)
            .eq('approval_status', 'approved')
            .is('deleted_at', null)
            .order('purchased_on', { ascending: false }),
        ),
        unwrap(
          await supabase
            .from('material_usage')
            .select('id, used_on, quantity, remarks, projects(id, name)')
            .eq('material_id', materialId)
            .eq('approval_status', 'approved')
            .is('deleted_at', null)
            .order('used_on', { ascending: false }),
        ),
        unwrap(
          await supabase
            .from('stock_transfers')
            .select('id, transferred_on, quantity, rate_paise, amount_paise, vehicle_no, driver_name, remarks, projects!stock_transfers_from_project_id_fkey(name), to_project:projects!stock_transfers_to_project_id_fkey(name)')
            .eq('material_id', materialId)
            .eq('approval_status', 'approved')
            .is('deleted_at', null)
            .order('transferred_on', { ascending: false }),
        ),
      ]);

      const purchases: MaterialPurchaseRow[] = purchaseRows.map((r) => {
        const advance = Number(r.advance_paid_paise ?? 0);
        return {
          id: r.id,
          date: r.purchased_on,
          projectId: (r.projects as unknown as { id: string; name: string } | null)?.id ?? '',
          projectName: (r.projects as unknown as { id: string; name: string } | null)?.name ?? '—',
          supplierName: (r.suppliers as unknown as { name: string } | null)?.name ?? '—',
          invoiceNo: r.invoice_no ?? null,
          advancePaidPaise: advance,
          balancePaise: Math.max(0, r.amount_paise - advance),
          quantity: Number(r.quantity),
          ratePaise: r.rate_paise,
          amountPaise: r.amount_paise,
        };
      });

      const totalPurchased = purchases.reduce((s, p) => s + p.quantity, 0);
      const totalUsed = usageRows.reduce((s, u) => s + Number(u.quantity), 0);
      const openingQty = Number(material.opening_qty);

      // A running stock balance: every purchase and usage entry, walked in
      // date order from the same opening quantity the Materials list starts
      // from, so "stock remaining after this entry" is an honest number
      // rather than the static, pre-seeded figure the prototype this page is
      // built from used. Purchase and usage on the same date are ordered
      // arbitrarily — this app only ever records a date, not a time.
      type Event = { date: string; delta: number; usageId?: string };
      const events: Event[] = [
        ...purchaseRows.map((r): Event => ({ date: r.purchased_on, delta: Number(r.quantity) })),
        ...usageRows.map((r): Event => ({ date: r.used_on, delta: -Number(r.quantity), usageId: r.id })),
      ].sort((a, b) => a.date.localeCompare(b.date));

      const balanceOf = new Map<string, number>();
      let running = openingQty;
      for (const e of events) {
        running += e.delta;
        if (e.usageId) balanceOf.set(e.usageId, running);
      }

      const usage: MaterialUsageRow[] = usageRows.map((r) => ({
        id: r.id,
        date: r.used_on,
        projectId: (r.projects as unknown as { id: string; name: string } | null)?.id ?? '',
        projectName: (r.projects as unknown as { id: string; name: string } | null)?.name ?? '—',
        quantity: Number(r.quantity),
        remarks: r.remarks ?? null,
        balance: balanceOf.get(r.id) ?? 0,
      }));

      const transfers: MaterialTransferRow[] = transferRows.map((r) => ({
        id: r.id,
        date: r.transferred_on,
        fromProjectName: (r.projects as unknown as { name: string } | null)?.name ?? '—',
        toProjectName: (r.to_project as unknown as { name: string } | null)?.name ?? '—',
        quantity: Number(r.quantity),
        ratePaise: r.rate_paise != null ? Number(r.rate_paise) : null,
        amountPaise: r.amount_paise != null ? Number(r.amount_paise) : null,
        vehicleNo: r.vehicle_no ?? null,
        driverName: r.driver_name ?? null,
        remarks: r.remarks ?? null,
      }));

      return {
        id: material.id,
        name: material.name,
        brand: material.brand,
        unitName: (material.units as unknown as { short_name: string } | null)?.short_name ?? null,
        supplierName: (material.suppliers as unknown as { name: string } | null)?.name ?? null,
        ratePaise: material.rate_paise,
        totalPurchased,
        totalUsed,
        stockOnHand: openingQty + totalPurchased - totalUsed,
        purchases,
        usage,
        transfers,
      };
    },
  });
}

/* --------------------------- sub-contractor detail --------------------------- */

export interface SubcontractorAttendanceRow {
  id: string;
  date: string;
  projectId: string;
  projectName: string;
  workType: string;
  workerCount: number;
  ratePerWorkerPaise: number | null;
  totalAmountPaise: number;
}

export interface SubcontractorPaymentRow {
  id: string;
  date: string;
  projectName: string | null;
  amountPaise: number;
  mode: string;
  /** What was still owed right after this payment — a running total, not this row's own amount. */
  balancePaise: number;
}

export interface SubcontractorProjectRow {
  id: string;
  name: string;
  clientName: string;
  status: string;
}

export interface SubcontractorDetail {
  id: string;
  name: string;
  phone: string;
  city: string | null;
  gstNumber: string | null;
  workTypes: string[];
  bankName: string | null;
  totalAttendancePaise: number;
  totalPaidPaise: number;
  pendingPaise: number;
  /** Headcount summed across every trade on every visit — one sub-contractor
   *  can bring several trades and several workers per trade on the same day. */
  totalWorkerDays: number;
  attendance: SubcontractorAttendanceRow[];
  payments: SubcontractorPaymentRow[];
  projects: SubcontractorProjectRow[];
}

/**
 * Everything a sub-contractor has done, and been paid, across every project.
 *
 * `subcontractor_labour` is read directly rather than through
 * `subcontractor_labour_batches` — a visit that covered several trades still
 * leaves one real row per trade, and that row carries its own
 * `approval_status`, so it is exactly as reviewable and exactly as real
 * whether it was entered on its own or as part of a batch. This history is
 * "what actually happened", not "what needs reviewing", so it does not need
 * the batch/line split `entry-sources.ts` uses for the approvals inbox.
 */
export function useSubcontractorDetail(subcontractorId: string) {
  return useQuery({
    queryKey: ['subcontractor', subcontractorId],
    enabled: !!subcontractorId,
    queryFn: async (): Promise<SubcontractorDetail> => {
      const sub = unwrap(
        await supabase
          .from('subcontractors')
          .select('id, name, phone, city, gst_number, bank_name, subcontractor_work_types(work_types(name))')
          .eq('id', subcontractorId)
          .is('deleted_at', null)
          .single(),
      );

      const [attendanceRows, paymentRows] = await Promise.all([
        unwrap(
          await supabase
            .from('subcontractor_labour')
            .select('id, worked_on, worker_count, rate_per_worker_paise, total_amount_paise, projects(id, name), work_types(name)')
            .eq('subcontractor_id', subcontractorId)
            .eq('approval_status', 'approved')
            .is('deleted_at', null)
            .order('worked_on', { ascending: false }),
        ),
        unwrap(
          await supabase
            .from('subcontractor_payments')
            .select('id, paid_on, amount_paise, mode, projects(name)')
            .eq('subcontractor_id', subcontractorId)
            .eq('approval_status', 'approved')
            .is('deleted_at', null)
            .order('paid_on', { ascending: true }),   // ascending: the running balance below needs chronological order
        ),
      ]);

      const attendance: SubcontractorAttendanceRow[] = attendanceRows.map((r) => ({
        id: r.id,
        date: r.worked_on,
        projectId: (r.projects as unknown as { id: string; name: string } | null)?.id ?? '',
        projectName: (r.projects as unknown as { id: string; name: string } | null)?.name ?? '—',
        workType: (r.work_types as unknown as { name: string } | null)?.name?.replace(/\s*\((?:Visit|Entry|Bill)\s*\d+\)/gi, '').trim() || '—',
        workerCount: r.worker_count,
        ratePerWorkerPaise: r.rate_per_worker_paise,
        totalAmountPaise: r.total_amount_paise,
      }));

      const totalAttendancePaise = attendance.reduce((s, a) => s + a.totalAmountPaise, 0);
      const totalWorkerDays = attendance.reduce((s, a) => s + a.workerCount, 0);
      const totalPaidPaise = paymentRows.reduce((s, p) => s + p.amount_paise, 0);

      // A running balance — what was still owed right after each payment —
      // built forward through time, then shown newest first like every other
      // ledger in this app.
      let remaining = totalAttendancePaise;
      const payments: SubcontractorPaymentRow[] = paymentRows
        .map((r) => {
          remaining -= r.amount_paise;
          return {
            id: r.id,
            date: r.paid_on,
            projectName: (r.projects as unknown as { name: string } | null)?.name ?? null,
            amountPaise: r.amount_paise,
            mode: r.mode,
            balancePaise: Math.max(0, remaining),
          };
        })
        .reverse();

      // Every project this sub-contractor has actually worked, newest visit
      // first — the same rule as a supplier's own project list.
      const seen = new Set<string>();
      const projectIds: string[] = [];
      for (const a of attendance) {
        if (a.projectId && !seen.has(a.projectId)) { seen.add(a.projectId); projectIds.push(a.projectId); }
      }
      let projects: SubcontractorProjectRow[] = [];
      if (projectIds.length) {
        const projectRows = unwrap(
          await supabase
            .from('projects')
            .select('id, name, status, clients(name)')
            .in('id', projectIds),
        );
        const byId = new Map(projectRows.map((p) => [p.id, p]));
        projects = projectIds
          .map((id) => byId.get(id))
          .filter((p): p is NonNullable<typeof p> => !!p)
          .map((p) => ({
            id: p.id,
            name: p.name,
            clientName: (p.clients as unknown as { name: string } | null)?.name ?? '—',
            status: p.status,
          }));
      }

      return {
        id: sub.id,
        name: sub.name,
        phone: sub.phone,
        city: sub.city,
        gstNumber: sub.gst_number,
        bankName: sub.bank_name,
        workTypes: (sub.subcontractor_work_types as unknown as Array<{ work_types: { name: string } | null }>)
          .map((x) => x.work_types?.name)
          .filter((n): n is string => !!n),
        totalAttendancePaise,
        totalPaidPaise,
        pendingPaise: Math.max(0, totalAttendancePaise - totalPaidPaise),
        totalWorkerDays,
        attendance,
        payments,
        projects,
      };
    },
  });
}

/** Material categories for the supplier multi-select and material form. */
export function useMaterialCategories() {
  return useQuery({
    queryKey: ['material-categories'],
    staleTime: 5 * 60_000,
    queryFn: async () =>
      unwrap(await supabase.from('material_categories')
        .select('id, name').eq('is_active', true).order('sort_order')) as Array<{ id: string; name: string }>,
  });
}

/** Create or retrieve custom work type for subcontractor specialisations */
export function useCreateWorkType() {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);

  return useMutation({
    mutationFn: async (name: string): Promise<{ id: string; name: string }> => {
      if (!actor) throw new Error('Not signed in');
      const trimmed = name.trim();
      if (!trimmed) throw new Error('Work type name is required');

      // Check if already exists for this company
      const { data: existing } = await supabase
        .from('work_types')
        .select('id, name')
        .eq('company_id', actor.companyId)
        .ilike('name', trimmed)
        .maybeSingle();

      if (existing) return existing as { id: string; name: string };

      const created = unwrap(
        await supabase
          .from('work_types')
          .insert({
            company_id: actor.companyId,
            name: trimmed,
            sort_order: 50,
          })
          .select('id, name')
          .single(),
      );

      return created as { id: string; name: string };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lookups'] });
    },
  });
}

/** Create or retrieve custom material category for supplier materials */
export function useCreateMaterialCategory() {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);

  return useMutation({
    mutationFn: async (name: string): Promise<{ id: string; name: string }> => {
      if (!actor) throw new Error('Not signed in');
      const trimmed = name.trim();
      if (!trimmed) throw new Error('Category name is required');

      // Check if already exists for this company
      const { data: existing } = await supabase
        .from('material_categories')
        .select('id, name')
        .eq('company_id', actor.companyId)
        .ilike('name', trimmed)
        .maybeSingle();

      if (existing) return existing as { id: string; name: string };

      const created = unwrap(
        await supabase
          .from('material_categories')
          .insert({
            company_id: actor.companyId,
            name: trimmed,
            sort_order: 50,
          })
          .select('id, name')
          .single(),
      );

      return created as { id: string; name: string };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['material-categories'] });
      void qc.invalidateQueries({ queryKey: ['lookups'] });
    },
  });
}

/* ----------------------- subcontractor_contracts ----------------------- */

export interface SubcontractorContractRow {
  id: string;
  subcontractorId: string;
  subcontractorName: string;
  projectId: string;
  projectName: string;
  clientId: string | null;
  clientName: string | null;
  contractAmountPaise: number;
  paidAmountPaise: number;
  pendingAmountPaise: number;
  status: string;
  remarks: string | null;
  createdAt: string;
}

export interface NewSubcontractorContract {
  subcontractorId: string;
  projectId: string;
  contractAmountPaise: number;
  remarks?: string;
  status?: 'planning' | 'ongoing' | 'on_hold' | 'completed';
}

export function useSubcontractorContracts(subcontractorId = '', projectId = '') {
  return useQuery({
    queryKey: ['subcontractor-contracts', subcontractorId, projectId],
    queryFn: async (): Promise<SubcontractorContractRow[]> => {
      let q = supabase
        .from('subcontractor_contracts')
        .select(`
          id, subcontractor_id, project_id, contract_amount_paise, status, description, created_at,
          projects(id, name, client_id, clients(id, name)),
          subcontractors(id, name)
        `)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (subcontractorId) q = q.eq('subcontractor_id', subcontractorId);
      if (projectId) q = q.eq('project_id', projectId);

      const rows = unwrap(await q);

      // Fetch payments to calculate paid and pending amounts per subcontractor & project
      let payQ = supabase
        .from('subcontractor_payments')
        .select('subcontractor_id, project_id, amount_paise')
        .is('deleted_at', null);

      if (subcontractorId) payQ = payQ.eq('subcontractor_id', subcontractorId);
      if (projectId) payQ = payQ.eq('project_id', projectId);

      const payRes = await payQ;
      const payments = payRes.data ?? [];

      const paidMap = new Map<string, number>();
      payments.forEach((p) => {
        const key = `${p.subcontractor_id}_${p.project_id || ''}`;
        paidMap.set(key, (paidMap.get(key) ?? 0) + Number(p.amount_paise || 0));
      });

      return rows.map((r) => {
        const proj = r.projects as unknown as { id: string; name: string; client_id: string | null; clients: { id: string; name: string } | null } | null;
        const sub = r.subcontractors as unknown as { id: string; name: string } | null;
        const key = `${r.subcontractor_id}_${r.project_id || ''}`;
        const paidAmountPaise = paidMap.get(key) ?? 0;
        const contractAmountPaise = Number(r.contract_amount_paise || 0);
        const pendingAmountPaise = Math.max(0, contractAmountPaise - paidAmountPaise);

        return {
          id: r.id,
          subcontractorId: r.subcontractor_id,
          subcontractorName: sub?.name ?? '—',
          projectId: r.project_id,
          projectName: proj?.name ?? '—',
          clientId: proj?.client_id ?? null,
          clientName: proj?.clients?.name ?? null,
          contractAmountPaise,
          paidAmountPaise,
          pendingAmountPaise,
          status: r.status,
          remarks: r.description,
          createdAt: r.created_at,
        };
      });
    },
  });
}

export function useCreateSubcontractorContract() {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);

  return useMutation({
    mutationFn: async (v: NewSubcontractorContract) => {
      if (!actor) throw new Error('Not signed in');
      if (!v.subcontractorId) throw new Error('Select a sub-contractor');
      if (!v.projectId) throw new Error('Select a project');

      const created = unwrap(
        await supabase
          .from('subcontractor_contracts')
          .insert({
            company_id: actor.companyId,
            subcontractor_id: v.subcontractorId,
            project_id: v.projectId,
            contract_amount_paise: v.contractAmountPaise,
            description: v.remarks?.trim() || null,
            status: v.status || 'ongoing',
            created_by: actor.id,
          })
          .select('id')
          .single(),
      );

      return created;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['subcontractor-contracts'] });
      void qc.invalidateQueries({ queryKey: ['subcontractors'] });
      void qc.invalidateQueries({ queryKey: ['subcontractor'] });
      void qc.invalidateQueries({ queryKey: ['subcontractor-balance-summary'] });
    },
  });
}

export function useUpdateSubcontractorContract() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...v }: Partial<NewSubcontractorContract> & { id: string }) => {
      const payload: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };
      if (v.contractAmountPaise !== undefined) payload.contract_amount_paise = v.contractAmountPaise;
      if (v.remarks !== undefined) payload.description = v.remarks?.trim() || null;
      if (v.status !== undefined) payload.status = v.status;
      if (v.projectId) payload.project_id = v.projectId;

      const { error } = await supabase
        .from('subcontractor_contracts')
        .update(payload)
        .eq('id', id);

      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['subcontractor-contracts'] });
      void qc.invalidateQueries({ queryKey: ['subcontractors'] });
      void qc.invalidateQueries({ queryKey: ['subcontractor'] });
      void qc.invalidateQueries({ queryKey: ['subcontractor-balance-summary'] });
    },
  });
}

export function useDeleteSubcontractorContract() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('subcontractor_contracts')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['subcontractor-contracts'] });
      void qc.invalidateQueries({ queryKey: ['subcontractors'] });
      void qc.invalidateQueries({ queryKey: ['subcontractor'] });
      void qc.invalidateQueries({ queryKey: ['subcontractor-balance-summary'] });
    },
  });
}
